import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { buildFinancialAidCorrectionPatch, filterFinancialAidCorrectionFields, flattenFinancialAidCorrectionFields, listFinancialAidCorrectionDraftPaths, mergeFinancialAidCorrectionSections, pruneFinancialAidCorrectionDraftSections, readFinancialAidCorrectionInputValue, readFinancialAidCorrectionPathValue, readCorrectionExpectedUpdatedAt, removeFinancialAidCorrectionDraftPath, resolveFinancialAidCorrectionFieldSelection, } from './financialAidCorrectionState.js';
const asRecord = (value) => value && typeof value === 'object' && !Array.isArray(value)
    ? value
    : {};
const errorMessage = (error) => error instanceof Error ? error.message : String(error ?? 'Unable to save corrections.');
const isOptimisticCorrectionConflict = (message) => /changed since it was loaded|refresh before saving corrections/i.test(message);
export const useFinancialAidCorrectionEditor = ({ application, includeTenantExtensions = false, onUpdate, onRefresh, onSaved, onSuccess, onError, }) => {
    const sections = useMemo(() => asRecord(application?.sections), [application?.sections]);
    const [isEditing, setIsEditing] = useState(false);
    const [correctionReason, setCorrectionReason] = useState('');
    const [draftSections, setDraftSections] = useState({});
    const [correctionFieldSearch, setCorrectionFieldSearch] = useState('');
    const [correctionFieldPath, setCorrectionFieldPath] = useState('');
    const [correctionFieldValue, setCorrectionFieldValue] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const applicationIdRef = useRef(application?.id);
    const effectiveSections = useMemo(() => asRecord(mergeFinancialAidCorrectionSections(sections, draftSections)), [draftSections, sections]);
    const correctionFields = useMemo(() => flattenFinancialAidCorrectionFields(sections, {
        academicYear: application?.academicYear,
        includeTenantExtensions,
    }), [application?.academicYear, includeTenantExtensions, sections]);
    const correctionFieldByPath = useMemo(() => new Map(correctionFields.map((field) => [field.path, field])), [correctionFields]);
    const visibleCorrectionFields = useMemo(() => filterFinancialAidCorrectionFields(correctionFields, correctionFieldSearch), [correctionFieldSearch, correctionFields]);
    const stagedCorrectionPaths = useMemo(() => listFinancialAidCorrectionDraftPaths(sections, draftSections), [draftSections, sections]);
    const stagedCorrectionPathSet = useMemo(() => new Set(stagedCorrectionPaths), [stagedCorrectionPaths]);
    const groupedCorrectionFields = useMemo(() => {
        const groups = new Map();
        visibleCorrectionFields.forEach((field) => {
            const group = field.group || 'Submitted fields';
            groups.set(group, [...(groups.get(group) ?? []), field]);
        });
        return Array.from(groups.entries());
    }, [visibleCorrectionFields]);
    const selectedCorrectionField = correctionFields.find((field) => field.path === correctionFieldPath) ?? null;
    const effectiveSelectedCorrectionInputValue = useMemo(() => correctionFieldPath
        ? readFinancialAidCorrectionInputValue(readFinancialAidCorrectionPathValue(effectiveSections, correctionFieldPath))
        : '', [correctionFieldPath, effectiveSections]);
    const stagedCorrectionSummaries = useMemo(() => stagedCorrectionPaths.map((path) => {
        const field = correctionFieldByPath.get(path);
        return {
            path,
            group: field?.group ?? 'Submitted fields',
            label: field?.label ?? path,
            beforeValue: readFinancialAidCorrectionInputValue(readFinancialAidCorrectionPathValue(sections, path)),
            afterValue: readFinancialAidCorrectionInputValue(readFinancialAidCorrectionPathValue(effectiveSections, path)),
        };
    }), [correctionFieldByPath, effectiveSections, sections, stagedCorrectionPaths]);
    const readCorrectionInputForPath = useCallback((path, fallback = '') => {
        if (!path)
            return '';
        if (stagedCorrectionPathSet.has(path)) {
            return readFinancialAidCorrectionInputValue(readFinancialAidCorrectionPathValue(effectiveSections, path));
        }
        return correctionFields.find((field) => field.path === path)?.inputValue ?? fallback;
    }, [correctionFields, effectiveSections, stagedCorrectionPathSet]);
    const setSelectedCorrectionPath = useCallback((path) => {
        setCorrectionFieldPath(path);
        setCorrectionFieldValue(readCorrectionInputForPath(path));
    }, [readCorrectionInputForPath]);
    useEffect(() => {
        if (!isEditing)
            return;
        const nextSelection = resolveFinancialAidCorrectionFieldSelection(correctionFields, visibleCorrectionFields, correctionFieldPath);
        const nextPath = nextSelection?.path ?? '';
        if (nextPath !== correctionFieldPath) {
            setCorrectionFieldPath(nextPath);
            setCorrectionFieldValue(readCorrectionInputForPath(nextPath, nextSelection?.inputValue ?? ''));
        }
    }, [
        correctionFieldPath,
        correctionFields,
        isEditing,
        readCorrectionInputForPath,
        visibleCorrectionFields,
    ]);
    useEffect(() => {
        if (applicationIdRef.current === application?.id) {
            return;
        }
        if (applicationIdRef.current == null) {
            applicationIdRef.current = application?.id;
            return;
        }
        applicationIdRef.current = application?.id;
        setIsEditing(false);
        setCorrectionReason('');
        setDraftSections({});
        setCorrectionFieldSearch('');
        setCorrectionFieldPath('');
        setCorrectionFieldValue('');
        setSaveError(null);
    }, [application?.id]);
    const beginEditing = useCallback(() => {
        const firstField = correctionFields[0] ?? null;
        if (!correctionFieldPath && firstField) {
            setCorrectionFieldPath(firstField.path);
            setCorrectionFieldValue(firstField.inputValue);
        }
        setSaveError(null);
        setIsEditing(true);
    }, [correctionFieldPath, correctionFields]);
    const cancelEditing = useCallback(() => {
        setIsEditing(false);
        setCorrectionReason('');
        setDraftSections({});
        setCorrectionFieldSearch('');
        setCorrectionFieldPath('');
        setCorrectionFieldValue('');
        setSaveError(null);
    }, []);
    const clearCorrectionDraftFields = useCallback(() => {
        setDraftSections({});
        if (correctionFieldPath) {
            setCorrectionFieldValue(readFinancialAidCorrectionInputValue(readFinancialAidCorrectionPathValue(sections, correctionFieldPath)));
        }
        setSaveError(null);
    }, [correctionFieldPath, sections]);
    const updateCorrectionSection = useCallback((sectionKey, patch) => {
        setDraftSections((current) => pruneFinancialAidCorrectionDraftSections(sections, {
            ...current,
            [sectionKey]: mergeFinancialAidCorrectionSections(current[sectionKey], patch),
        }));
        setSaveError(null);
    }, [sections]);
    const canApplyCorrection = useMemo(() => {
        if (!selectedCorrectionField || !correctionFieldPath)
            return false;
        if (correctionFieldValue === effectiveSelectedCorrectionInputValue)
            return false;
        try {
            buildFinancialAidCorrectionPatch(effectiveSections, correctionFieldPath, correctionFieldValue);
            return true;
        }
        catch {
            return false;
        }
    }, [
        correctionFieldPath,
        correctionFieldValue,
        effectiveSections,
        effectiveSelectedCorrectionInputValue,
        selectedCorrectionField,
    ]);
    const applySelectedCorrectionField = useCallback(() => {
        if (!canApplyCorrection || !correctionFieldPath)
            return;
        try {
            const { sectionKey, patch } = buildFinancialAidCorrectionPatch(effectiveSections, correctionFieldPath, correctionFieldValue);
            setDraftSections((current) => pruneFinancialAidCorrectionDraftSections(sections, {
                ...current,
                [sectionKey]: mergeFinancialAidCorrectionSections(current[sectionKey], patch),
            }));
            setSaveError(null);
        }
        catch (error) {
            const message = errorMessage(error);
            setSaveError(message);
            onError?.(message);
        }
    }, [
        canApplyCorrection,
        correctionFieldPath,
        correctionFieldValue,
        effectiveSections,
        onError,
        sections,
    ]);
    const unstageCorrectionPath = useCallback((path) => {
        setDraftSections((current) => removeFinancialAidCorrectionDraftPath(sections, current, path));
        if (path === correctionFieldPath) {
            setCorrectionFieldValue(readFinancialAidCorrectionInputValue(readFinancialAidCorrectionPathValue(sections, path)));
        }
        setSaveError(null);
    }, [correctionFieldPath, sections]);
    const saveDisabled = isSaving || correctionReason.trim().length === 0 || stagedCorrectionPaths.length === 0;
    const saveCorrection = useCallback(async () => {
        if (!application || saveDisabled)
            return;
        if (!onUpdate) {
            const message = 'Financial aid correction transport is unavailable.';
            setSaveError(message);
            onError?.(message);
            return;
        }
        const expectedUpdatedAt = readCorrectionExpectedUpdatedAt(application.updatedAt);
        if (expectedUpdatedAt == null) {
            const message = 'Application version is unavailable. Refresh before saving corrections.';
            setSaveError(message);
            onError?.(message);
            return;
        }
        setIsSaving(true);
        setSaveError(null);
        try {
            const updated = await onUpdate({
                id: application.id,
                correctionMode: 'staff_correction',
                correctionReason: correctionReason.trim(),
                expectedUpdatedAt,
                sections: draftSections,
            });
            await onSaved?.(updated);
            onSuccess?.('Application corrections saved.');
            cancelEditing();
            await onRefresh();
        }
        catch (error) {
            const rawMessage = errorMessage(error);
            const message = isOptimisticCorrectionConflict(rawMessage)
                ? 'Application changed and was refreshed. Review the staged correction before retrying.'
                : rawMessage;
            if (isOptimisticCorrectionConflict(rawMessage)) {
                await onRefresh();
            }
            setSaveError(message);
            onError?.(message);
        }
        finally {
            setIsSaving(false);
        }
    }, [
        application,
        cancelEditing,
        correctionReason,
        draftSections,
        onError,
        onRefresh,
        onSaved,
        onSuccess,
        onUpdate,
        saveDisabled,
    ]);
    return {
        application,
        sections,
        isEditing,
        isSaving,
        saveDisabled,
        saveError,
        correctionReason,
        draftSections,
        correctionFieldSearch,
        correctionFieldPath,
        correctionFieldValue,
        correctionFields,
        visibleCorrectionFields,
        groupedCorrectionFields,
        selectedCorrectionField,
        stagedCorrectionPaths,
        stagedCorrectionPathSet,
        stagedCorrectionSummaries,
        canApplyCorrection,
        setCorrectionReason,
        setCorrectionFieldSearch,
        setCorrectionFieldValue,
        setSelectedCorrectionPath,
        beginEditing,
        cancelEditing,
        clearCorrectionDraftFields,
        updateCorrectionSection,
        applySelectedCorrectionField,
        unstageCorrectionPath,
        saveCorrection,
    };
};
//# sourceMappingURL=useFinancialAidCorrectionEditor.js.map